<template>
    <v-dialog v-model="visible" fullscreen>
        <v-card class="et-card">
            <v-card-title class="et-topo">
                <v-btn icon @click="onClickGoBack()">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <v-spacer />
                <span style="text-transform: uppercase;">
                    <b>{{ produtoTitulo }}</b>
                </span>
                <v-spacer />
            </v-card-title>

            <div ref="corpo" class="et-corpo">
                <EtapaProgresso :etapas="etapas" :indice="etapaAtual" />

                <div v-if="passo" class="et-cab">
                    <div class="et-cab__linha">
                        <div class="et-cab__txt">
                            <!-- Combo: de qual item desse combo é essa escolha? -->
                            <div v-if="contexto" class="et-cab__ctx">{{ contexto }}</div>
                            <div class="et-cab__tit">{{ passo.titulo }}</div>
                            <div class="et-cab__sub">{{ instrucao }}</div>
                        </div>
                        <EtapaSelo :passo="passo" />
                    </div>

                    <!-- Só quando existe adicional: "R$ 0,00" não é informação. -->
                    <div v-if="valorAdicional" class="et-cab__adicional">
                        Valor adicional: {{ moeda() }} {{ formatPrice(valorAdicional) }}
                    </div>

                    <div v-if="categorias.length > 1" class="et-cab__categorias">
                        <v-btn
                            :outlined="!!categoriaFilter"
                            color="primary"
                            class="mr-1"
                            depressed
                            tile
                            small
                            @click="categoriaFilter = ''"
                        >
                            Todas
                        </v-btn>
                        <v-btn
                            v-for="categoria in categorias" :key="categoria"
                            :outlined="categoriaFilter !== categoria"
                            color="primary"
                            class="mr-1"
                            min-width="100px"
                            depressed
                            tile
                            small
                            @click="categoriaFilter = categoria"
                        >
                            {{ categoria }}
                        </v-btn>
                    </div>

                    <div v-if="showPesquisa" class="et-cab__busca">
                        <v-text-field
                            v-model="search"
                            dense
                            outlined
                            clearable
                            hide-details
                            label="Digite para pesquisar..."
                            prepend-inner-icon="mdi-magnify"
                        />
                    </div>
                </div>

                <div class="et-opcoes">
                    <EtapaOpcao
                        v-for="complemento in complementosFiltrados"
                        :key="complemento.id || complemento.descricao"
                        :passo="passo"
                        :complemento="complemento"
                        :chip-estoque="getEstoqueText(complemento)"
                        :editavel="qtdMaxDoComplemento(passo, complemento) > 1"
                        @tocar="onTocar"
                        @somar="onSomar"
                        @remover="onRemover"
                        @quantidade="onChangeQuantidade"
                    />

                    <div v-if="!complementosFiltrados.length" class="et-opcoes__vazio">
                        Nenhuma opção encontrada.
                    </div>
                </div>
            </div>

            <div class="et-rodape">
                <v-btn
                    color="primary"
                    large
                    block
                    class="white--text et-cta"
                    :disabled="!!trava"
                    @click="onClickNext()"
                >
                    <span class="et-cta__txt">{{ rotuloDoBotao }}</span>
                    <v-icon v-if="proximoProduto && !trava" right>mdi-arrow-right</v-icon>
                </v-btn>
            </div>
        </v-card>
    </v-dialog>
</template>

<script>
import EtapaOpcao from '@/components/EtapaOpcao';
import EtapaProgresso from '@/components/EtapaProgresso';
import EtapaSelo from '@/components/EtapaSelo';
import { valorTotalComplementos } from '@/utils/calc';
import { clone } from '@/utils/utils';
import {
    definirQuantidade,
    escolhaUnica,
    etapasDoProduto,
    instrucaoDaEtapa,
    isIndisponivel,
    limiteMaxEtapa,
    limparEscolhasVazias,
    pendenciaDaEtapa,
    qtdMaxDoComplemento,
    remover,
    somar,
    tocar,
    zerarEscolhas,
} from '@/utils/etapa';

export default {
    name: 'Steps',

    components: { EtapaOpcao, EtapaProgresso, EtapaSelo },

    props: {
        visible: {
            type: Boolean,
            default: false
        },

        value: {
            type: Object,
        },
    },

    data: () => ({
        search: '',
        produto: null,
        etapaAtual: 0,
        categoriaFilter: '',
        finalizando: false,
    }),

    watch: {
        visible(v) {
            v && this.initialize();

            !v && this.$emit('close');
        },
    },

    computed: {
        /** Etapas do produto E dos sub-produtos do combo, achatadas numa lista só. */
        etapas() {
            return etapasDoProduto(this.produto);
        },

        etapa() {
            return this.etapas[this.etapaAtual] || null;
        },

        passo() {
            return this.etapa?.passo || null;
        },

        contexto() {
            return this.etapa?.contexto || '';
        },

        produtoTitulo() {
            return this.produto?.descricao || '';
        },

        instrucao() {
            return instrucaoDaEtapa(this.passo);
        },

        ehUltimaEtapa() {
            return this.etapaAtual >= this.etapas.length - 1;
        },

        proximoProduto() {
            const proxima = this.etapas[this.etapaAtual + 1];

            if (!proxima?.contexto || proxima.contexto === this.contexto) {
                return null;
            }

            return proxima.contexto;
        },

        rotuloDoBotao() {
            if (this.trava) {
                return this.trava;
            }

            if (this.proximoProduto) {
                return `Ir para ${this.proximoProduto}`;
            }

            return this.ehUltimaEtapa ? 'Concluir' : 'Continuar';
        },

        /** Motivo para não avançar, já escrito para quem está na tela, ou `null`. */
        trava() {
            return this.passo ? pendenciaDaEtapa(this.passo) : null;
        },

        valorAdicional() {
            return this.passo ? valorTotalComplementos(this.passo) : 0;
        },

        complementos() {
            return this.passo?.complementos || [];
        },

        complementosFiltrados() {
            if (this.search) {
                const termo = this.search.trim().toLowerCase();
                return this.complementos.filter(c => c.descricao.trim().toLowerCase().includes(termo));
            }

            return this.categoriaFilter
                ? this.complementos.filter(c => c.categoria?.descricao === this.categoriaFilter)
                : this.complementos;
        },

        categorias() {
            const uniques = [];

            this.complementos.forEach(c => {
                const descricao = c.categoria?.descricao;
                descricao && !uniques.includes(descricao) && uniques.push(descricao);
            });

            return uniques;
        },

        showPesquisa() {
            return this.complementos.length > 10;
        },
    },

    methods: {
        initialize() {
            this.produto = zerarEscolhas(clone(this.value || {}));
            this.etapaAtual = 0;
            this.finalizando = false;
            this.limparFiltros();
        },

        qtdMaxDoComplemento(passo, complemento) {
            return qtdMaxDoComplemento(passo, complemento);
        },

        async confirmarIndisponivel(complemento) {
            if (!isIndisponivel(complemento)) {
                return true;
            }

            const msg = 'Item indisponível, deseja adicionar mesmo assim?';

            return await this.$root.$confirm('Confirmação', msg, { color: 'red darken-1' });
        },

        async onTocar(complemento) {
            if (escolhaUnica(this.passo) && +complemento.quantidade > 0) {
                return;
            }

            if (!await this.confirmarIndisponivel(complemento)) {
                return;
            }

            tocar(this.passo, complemento) || this.notify(this.motivoDoBloqueio(complemento), 'warning');
        },

        async onSomar(complemento) {
            if (!await this.confirmarIndisponivel(complemento)) {
                return;
            }

            somar(this.passo, complemento) || this.notify(this.motivoDoBloqueio(complemento), 'warning');
        },

        onRemover(complemento) {
            remover(complemento);
        },

        async onChangeQuantidade({ complemento, quantidade }) {
            const aumentou = quantidade > (+complemento.quantidade || 0);

            if (aumentou && !await this.confirmarIndisponivel(complemento)) {
                return;
            }

            const { aviso } = definirQuantidade(this.passo, complemento, quantidade);

            aviso && this.notify(aviso, 'warning');
        },

        motivoDoBloqueio(complemento) {
            const tetoDoItem = qtdMaxDoComplemento(this.passo, complemento);

            return (+complemento.quantidade || 0) >= tetoDoItem
                ? `Máximo de ${tetoDoItem} para ${complemento.descricao}`
                : `Máximo de ${limiteMaxEtapa(this.passo)} nesta etapa`;
        },

        onClickNext() {
            if (this.trava) {
                return;
            }

            this.limparFiltros();

            if (!this.ehUltimaEtapa) {
                this.etapaAtual++;
                this.scrollTop();
                return;
            }

            this.finalizar();
        },

        finalizar() {
            if (this.finalizando) {
                return;
            }

            this.finalizando = true;

            this.$emit('finish', limparEscolhasVazias(clone(this.produto)));
        },

        onClickGoBack() {
            if (this.etapaAtual === 0) {
                this.$emit('close');
                return;
            }

            this.limparFiltros();
            this.etapaAtual--;
            this.scrollTop();
        },

        limparFiltros() {
            this.search = '';
            this.categoriaFilter = '';
        },

        scrollTop() {
            this.$refs.corpo && (this.$refs.corpo.scrollTop = 0);
        },

        getEstoqueText(complemento) {
            if (!this.controleEstoqueAtivo() || !complemento.controle_estoque || complemento.estoque <= 0) {
                return '';
            }

            return complemento.estoque > 1
                ? `Restam ${complemento.estoque} unidades`
                : 'Resta apenas 1 unidade';
        },
    }
}
</script>

<style scoped>
.et-card {
    display: flex;
    flex-direction: column;
    height: 100%;
}
.et-topo { flex: none; }
.et-corpo { flex: 1; overflow-y: auto; }
.et-rodape {
    flex: none;
    padding: 12px 16px;
    border-top: 1px solid rgba(128, 128, 128, 0.2);
}

.et-cta {
    height: auto !important;
    min-height: 44px;
    flex-shrink: 1;
    max-width: 100%;
}
.et-cta >>> .v-btn__content {
    flex: 1 1 auto;
    min-width: 0;
    padding: 8px 0;
}
.et-cta__txt {
    min-width: 0;
    white-space: normal;
    line-height: 1.25;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.et-cab {
    padding: 14px 16px 4px;
    max-width: 900px;
    margin: 0 auto;
}
.et-cab__linha {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
}
.et-cab__txt { flex: 1; min-width: 0; }
.et-cab__ctx {
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: #8b9098;
    margin-bottom: 2px;
}
.et-cab__tit { font-size: 20px; font-weight: 800; line-height: 1.2; }
.et-cab__sub { font-size: 13px; color: #6b7178; margin-top: 2px; }
.theme--dark .et-cab__sub { color: #b0b6bd; }
.et-cab__adicional { font-size: 13px; color: #0f8a3d; font-weight: 700; margin-top: 6px; }

.et-cab__categorias {
    display: flex;
    overflow-x: auto;
    white-space: nowrap;
    padding: 10px 0 2px;
    gap: 2px;
}
.et-cab__busca { margin-top: 10px; }

.et-opcoes {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 9px;
    align-content: start;
    padding: 8px 16px 16px;
    max-width: 900px;
    margin: 0 auto;
}
@media (min-width: 720px) {
    .et-opcoes { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
}
.et-opcoes__vazio {
    font-size: 13.5px;
    color: #8b9098;
    font-style: italic;
    padding: 12px 2px;
}
</style>
